/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type {
  ActionConfig,
  ActionReference,
  Editor,
  Member,
  Team,
  TriggerReference,
  ValueConfig_V,
  ValueDescription_T,
  TriggerConfig,
} from "@smartsuite/automation-engine-api";
import type { Fields } from "@smartsuite/smartsuite-client";
import * as R from "remeda";
import type {
  InputCandidate,
  TriggerCandidate,
} from "../../../../../../../../types/candidate";
import { getOutputDescriptions } from "../../../../../../../../utils/conditions-coverter.hook";
import {
  generalTypeFilter,
  isSimpleTextOutput,
  nameToInitials,
  ssTypeFilter,
  systemTypeFilter,
} from "../../../../utils";
import type { FieldTypePillProps } from "@smartsuite/react-ui/lib";
import { usePillTooltip } from "../../../../../../../../utils/make-tooltip.hook";
import { rootStore } from "../../../../../../../../stores";
import { findMatchedAction } from "../../../../../../../../utils/match-action";
import type { LoopingOptionGroup } from "../../../../MemberEditor/utils";
import { t } from "i18next";
import { CURRENT_ITEM_OUTPUT_ID } from "../../../../../../../../utils/constants";
import type { LoopScope } from "../../../../../../../../stores/automation/looping.store";
import { currentItemOutputsRefPill } from "../../../../utils/current-item-outputs-ref-pill";

export interface TriggerActionOption {
  title: string;
  outputId: string;
  icon?: string;
  hash?: string;
  contextSpecific: boolean;
}

export interface ActionOptionGroup {
  name: string;
  options: TriggerActionOption[];
  action: ActionConfig;
  hideNamePrefix?: boolean;
}

export interface PhonePill extends FieldTypePillProps {
  type:
    | "actionOutput"
    | "triggerOutput"
    | "member"
    | "team"
    | "custom"
    | "loop";
  value: string;
}

export const triggerToOptions = (
  trigger: TriggerCandidate,
  generalTypes?: ValueDescription_T[],
  ssTypes?: ValueDescription_T[],
  ssFieldTypes?: Fields[],
  systemAllowedOptions?: string[],
): TriggerActionOption[] =>
  getOutputDescriptions(trigger)
    .filter((output) => {
      // support teamfield entire field
      if (
        output.smartsuiteFieldType === "teamfield" &&
        !output.outputId.includes(".")
      ) {
        return true;
      }
      if (isSimpleTextOutput(output)) {
        return true;
      }
      if (output.contextSpecific) {
        return systemTypeFilter(systemAllowedOptions, output);
      }
      if (output.smartsuiteFieldType !== undefined) {
        return ssTypeFilter(
          output.smartsuiteFieldType,
          ssFieldTypes,
          ssTypes,
          output,
        );
      }

      return generalTypeFilter(generalTypes, output);
    })
    .map(
      (output: {
        label: string;
        outputId: string;
        icon: string;
        contextSpecific: boolean;
      }): TriggerActionOption => ({
        title: output.label,
        outputId: output.outputId,
        icon: output.icon,
        contextSpecific: output.contextSpecific,
      }),
    );

interface ConvertersFunctions {
  convertInputToPills: (
    input: InputCandidate,
    members: Member[],
    teams: Team[],
    triggerOptions: TriggerActionOption[],
    actionOptions: ActionOptionGroup[],
    trigger: TriggerConfig,
    actions: ActionConfig[],
    editor: Editor,
    loopOptions?: LoopingOptionGroup,
  ) => PhonePill[];
}

export const useConverters = (): ConvertersFunctions => {
  const {
    makeTriggerPillTooltip,
    makeActionPillTooltip,
    makeCurrentItemPillTooltip,
  } = usePillTooltip();

  const convertInputToPills = (
    input: InputCandidate,
    members: Member[],
    teams: Team[],
    triggerOptions: TriggerActionOption[],
    actionOptions: ActionOptionGroup[],
    trigger: TriggerConfig,
    actions: ActionConfig[],
    editor: Editor,
    loopOptions?: LoopingOptionGroup,
  ): PhonePill[] => {
    return (
      input.value?.values.flatMap((value): PhonePill[] => {
        switch (value.value.oneofKind) {
          case "reference":
            if (value.value.reference.outputId === CURRENT_ITEM_OUTPUT_ID) {
              return [
                {
                  type: "loop",
                  value: JSON.stringify(value.value.reference),
                  icon: "",
                  title: t("general.current-item"),
                  tooltip: makeCurrentItemPillTooltip(
                    rootStore.automations.candidate.looping,
                  ),
                },
              ];
            }
            if (value.value.reference.source.oneofKind === "triggerReference") {
              const { outputId } = value.value.reference;
              let option = triggerOptions.find(
                (option) => option.outputId === outputId,
              );

              if (option === undefined && loopOptions?.options.length) {
                option = loopOptions.options.find(
                  (option) => option.outputId === outputId,
                );
              }

              return [
                {
                  type: "triggerOutput",
                  value: outputId,
                  icon: option?.icon ?? "",
                  title:
                    option?.title ??
                    String(t("automation-form.deleted-field-title")),
                  tooltip: makeTriggerPillTooltip(trigger),
                  isError: !option?.title,
                },
              ];
            } else if (
              value.value.reference.source.oneofKind === "actionReference"
            ) {
              const { outputId } = value.value.reference;
              let option = findActionOption(actionOptions, outputId);

              if (option === undefined && loopOptions?.options.length) {
                option = loopOptions.options.find(
                  (option) => option.outputId === outputId,
                );
              }

              const pillAction = findMatchedAction(
                value.value.reference.source,
                actions,
              );
              return [
                {
                  type: "actionOutput",
                  value: outputId,
                  icon: option?.icon ?? "",
                  isError: !option?.title,
                  title:
                    option?.title ??
                    String(t("automation-form.deleted-field-title")),
                  tooltip:
                    pillAction === undefined
                      ? undefined
                      : makeActionPillTooltip(pillAction),
                },
              ];
            }
            return [];
          case "function":
            if (value.value.function.function.oneofKind === "memberPhone") {
              const { memberId } = value.value.function.function.memberPhone;
              const member = members.find(
                (member) => member.memberId === memberId,
              );
              const title = R.isTruthy(member)
                ? R.isTruthy(member.name)
                  ? member.name
                  : member.email[0]
                : "";
              return [
                {
                  type: "member",
                  value: memberId,
                  avatar: {
                    initials: nameToInitials(title),
                    image: member?.profileImageUrl,
                  },
                  title,
                },
              ];
            } else if (
              value.value.function.function.oneofKind === "teamPhones"
            ) {
              const { teamId } = value.value.function.function.teamPhones;
              const title = R.pipe(
                teams,
                R.find((team) => team.teamId === teamId),
                (team) => team?.name ?? "",
              );
              return [
                {
                  type: "team",
                  value: teamId,
                  avatar: {
                    initials: nameToInitials(title),
                  },
                  title,
                },
              ];
            } else {
              return [];
            }
          case "string": {
            const { string: phone } = value.value;
            return [
              {
                type: "custom",
                value: phone,
                title: phone,
                isError: !getPhoneValidation(editor)(phone),
              },
            ];
          }
          default:
            return [];
        }
      }) ?? []
    );
  };

  return {
    convertInputToPills,
  };
};

const findActionOption = (
  actionOptions: ActionOptionGroup[],
  outputId: string,
): TriggerActionOption | undefined => {
  let result: TriggerActionOption | undefined;
  for (const action of actionOptions) {
    for (const option of action.options) {
      if (option.outputId === outputId) {
        result = option;
        break;
      }
      if (result !== undefined) break;
    }
  }
  return result;
};

export const mergeNextPillToInput = (
  input: InputCandidate,
  pill:
    | {
        type: "triggerOutput";
        triggerReference: TriggerReference;
        outputId: string;
      }
    | {
        type: "actionOutput";
        actionReference: ActionReference;
        outputId: string;
      }
    | { type: "member"; memberId: string }
    | { type: "team"; teamId: string }
    | {
        type: "custom";
        phone: string;
      }
    | { type: "loop"; looping: LoopScope; outputId: string },
  multiple: boolean,
  editor: Editor,
): InputCandidate => {
  const newInput = {
    ...input,
    value: {
      values: [
        ...(multiple && R.isTruthy(input.value) ? input.value.values : []),
        ...(() => {
          switch (pill.type) {
            case "triggerOutput":
              return [
                {
                  value: {
                    oneofKind: "reference",
                    reference: {
                      source: {
                        oneofKind: "triggerReference",
                        triggerReference: pill.triggerReference,
                      },
                      outputId: pill.outputId,
                    },
                  } as const,
                },
              ];
            case "actionOutput":
              return [
                {
                  value: {
                    oneofKind: "reference",
                    reference: {
                      source: {
                        oneofKind: "actionReference",
                        actionReference: pill.actionReference,
                      },
                      outputId: pill.outputId,
                    },
                  } as const,
                },
              ];
            case "member":
              return [
                {
                  value: {
                    oneofKind: "function",
                    function: {
                      function: {
                        oneofKind: "memberPhone",
                        memberPhone: { memberId: pill.memberId },
                      },
                    },
                  } as const,
                },
              ];
            case "team":
              return [
                {
                  value: {
                    oneofKind: "function",
                    function: {
                      function: {
                        oneofKind: "teamPhones",
                        teamPhones: { teamId: pill.teamId },
                      },
                    },
                  } as const,
                },
              ];
            case "custom":
              if (multiple) {
                const phones = pill.phone.split(/,|, |;|; /);
                return phones
                  .filter((phone) => getPhoneValidation(editor)(phone))
                  .map((phone) => ({
                    value: {
                      oneofKind: "string",
                      string: phone.toLowerCase(),
                    } as const,
                  }));
              } else {
                return getPhoneValidation(editor)(pill.phone)
                  ? [
                      {
                        value: {
                          oneofKind: "string",
                          string: pill.phone.toLowerCase(),
                        } as const,
                      },
                    ]
                  : [];
              }
            case "loop":
              // If the source is a array from webhook
              if (pill.looping.source?.oneofKind === "outputReference") {
                return currentItemOutputsRefPill(pill.looping);
              }

              // If the source is a find action
              if (
                pill.looping.source?.oneofKind === "recordList" &&
                pill.looping.source.recordList.recordList.oneofKind ===
                  "actionReference"
              ) {
                return [
                  {
                    value: {
                      oneofKind: "reference",
                      reference: {
                        source: {
                          oneofKind: "actionReference",
                          actionReference:
                            pill.looping.source.recordList.recordList
                              .actionReference,
                        },
                        outputId: pill.outputId,
                      },
                    } as const,
                  },
                ];
              }
              return [];
            default:
              return [];
          }
        })(),
      ],
    },
  };
  newInput.validationError = validatePhoneValues(newInput.value.values, editor);
  return newInput;
};

export const ejectPillFromInput = (
  input: InputCandidate,
  index: number,
  editor: Editor,
): InputCandidate => {
  const newInput = {
    ...input,
    value: {
      values:
        input.value?.values.filter((_: any, i: number) => i !== index) ?? [],
    },
  };
  newInput.validationError = validatePhoneValues(newInput.value.values, editor);
  return newInput;
};

const validatePhoneValues = (
  values: ValueConfig_V[],
  editor: Editor,
): string => {
  const isPhoneValid = getPhoneValidation(editor);
  if (
    values.some((value) => {
      return (
        value.value.oneofKind === "string" && !isPhoneValid(value.value.string)
      );
    })
  ) {
    return "The field is not valid";
  }

  return "";
};

const getPhoneValidation = (editor: Editor): ((phone: string) => boolean) => {
  if (editor?.validation.oneofKind === "regex") {
    const { regex } = editor.validation;
    return (phone: string) => RegExp(regex).test(phone.toLowerCase());
  } else {
    return (_: string) => true;
  }
};
